import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import { DataGridPro, type GridColDef, type GridRowParams } from '@mui/x-data-grid-pro';

import { useGetActionRewardConfigs } from '~/hooks/apis/loyalty/useGetActionRewardConfigs';

import { ACTION_REWARD_MIN_MAX_REPEAT, type ActionRewardConfig } from '~/types/loyaltyContract';

import ActionRewardConfigFormDialog from './ActionRewardConfigFormDialog';
import ActionRewardEarnKeyPointsDialog from './ActionRewardEarnKeyPointsDialog';
import ActionRewardToolbar from './ActionRewardToolbar';
import { getActionRewardFeatureToggleLabels } from './actionRewardFeatureToggles';

const DEFAULT_COLUMN_VISIBILITY = {
  title: false,
  description: false,
  activityDescription: false,
  excludeColumn: false,
  updatedAt: false,
  rewardType: false,
  createdAt: false,
  allowRepeat: false,
  maxRepeat: false,
};

function statusChipColor(status: string): 'success' | 'default' {
  return status === 'ACTIVE' ? 'success' : 'default';
}

function hasInvalidRepeatability(config: ActionRewardConfig): boolean {
  if (!config.allowRepeat) {
    return config.maxRepeat != null;
  }
  return config.maxRepeat != null && config.maxRepeat < ACTION_REWARD_MIN_MAX_REPEAT;
}

function getRepeatabilityDisplay(config: ActionRewardConfig) {
  if (hasInvalidRepeatability(config)) {
    return {
      label: 'Invalid',
      color: 'error' as const,
    };
  }
  if (!config.allowRepeat) {
    return {
      label: 'No',
      color: 'default' as const,
    };
  }
  if (config.maxRepeat == null) {
    return {
      label: 'Unlimited',
      color: 'warning' as const,
    };
  }
  return {
    label: `Up to ${config.maxRepeat}`,
    color: 'success' as const,
  };
}

export default function ActionRewardPage() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError } = useGetActionRewardConfigs();
  const rows = data?.result ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<ActionRewardConfig | undefined>(undefined);
  const [pointsEarnKey, setPointsEarnKey] = useState<string | null>(null);

  useEffect(() => {
    const configs = data?.result;
    if (!configs) {
      return;
    }
    const invalid = configs.filter(hasInvalidRepeatability);
    if (invalid.length === 0) {
      return;
    }
    const names = invalid.map((config) => config.actionName).join(', ');
    enqueueSnackbar(
      invalid.length === 1
        ? `${names} has a repeat configuration that does not make sense.`
        : `${invalid.length} action reward configs have a repeat configuration that does not make sense: ${names}`,
      { variant: 'warning' },
    );
  }, [data?.result, enqueueSnackbar]);

  const columns = useMemo<Array<GridColDef<ActionRewardConfig>>>(
    () => [
      { field: 'id', headerName: 'ID', width: 96 },
      { field: 'actionName', headerName: 'Action name', flex: 1, minWidth: 140 },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={row.status === 'ACTIVE' ? 'Active' : 'Inactive'}
            color={statusChipColor(row.status)}
          />
        ),
      },
      { field: 'groupType', headerName: 'Group type', width: 130 },
      {
        field: 'earnKey',
        headerName: 'Earn key',
        flex: 1,
        minWidth: 140,
        display: 'flex',
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%', minWidth: 0, height: '100%' }}>
            <Typography variant="body2" noWrap>
              {row.earnKey}
            </Typography>
            {row.earnKey ? (
              <IconButton
                size="small"
                aria-label={`View points for ${row.earnKey}`}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  setPointsEarnKey(row.earnKey);
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        ),
      },
      {
        field: 'repeatability',
        headerName: 'Repeatability',
        width: 150,
        sortable: true,
        valueGetter: (_value, row) => getRepeatabilityDisplay(row).label,
        renderCell: ({ row }) => {
          const display = getRepeatabilityDisplay(row);
          return <Chip size="small" label={display.label} color={display.color} />;
        },
      },
      {
        field: 'featureToggles',
        headerName: 'Feature toggles',
        width: 180,
        display: 'flex',
        valueGetter: (_value, row) => getActionRewardFeatureToggleLabels(row).join(', '),
        renderCell: ({ row }) => {
          const labels = getActionRewardFeatureToggleLabels(row);
          if (labels.length === 0) {
            return null;
          }
          return (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ width: '100%', minWidth: 0, height: '100%' }}
            >
              {labels.map((label) => (
                <Chip key={label} size="small" label={label} />
              ))}
            </Stack>
          );
        },
      },
      { field: 'title', headerName: 'Title', flex: 1, minWidth: 140 },
      { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
      { field: 'activityDescription', headerName: 'Activity description', flex: 1, minWidth: 180 },
      { field: 'excludeColumn', headerName: 'Exclude column', width: 180 },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 140,
        valueFormatter: (value: string) => (value ? dayjs(value).tz('Australia/Sydney').format('DD/MM/YYYY') : '—'),
      },
      { field: 'rewardType', headerName: 'Reward type', width: 140 },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 140,
        valueFormatter: (value: string) => (value ? dayjs(value).tz('Australia/Sydney').format('DD/MM/YYYY') : '—'),
      },
      {
        field: 'allowRepeat',
        headerName: 'Allow repeat',
        width: 130,
        type: 'boolean',
      },
      {
        field: 'maxRepeat',
        headerName: 'Max repeat',
        width: 120,
        valueFormatter: (value: number | null | undefined) => (value == null ? '—' : value),
      },
    ],
    [],
  );

  const openCreate = () => {
    setSelected(undefined);
    setDialogOpen(true);
  };

  const handleRowClick = (params: GridRowParams<ActionRewardConfig>) => {
    setSelected(params.row);
    setDialogOpen(true);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ my: 2 }}>
        <Box>
          <Typography variant="h6">Action Reward</Typography>
          <Typography variant="caption" color="text.secondary">
            Manage action-reward configurations
          </Typography>
        </Box>
        <Button variant="contained" color="primary" onClick={openCreate}>
          Create
        </Button>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load action reward configs. Please try again.
        </Alert>
      )}

      <Paper>
        <Box sx={{ height: 640 }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.id}
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              columns: { columnVisibilityModel: DEFAULT_COLUMN_VISIBILITY },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            slots={{ toolbar: ActionRewardToolbar }}
            density="comfortable"
            sx={{
              '& .MuiDataGrid-row:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                cursor: 'pointer',
              },
            }}
          />
        </Box>
      </Paper>

      <ActionRewardConfigFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} config={selected} />
      <ActionRewardEarnKeyPointsDialog earnKey={pointsEarnKey} onClose={() => setPointsEarnKey(null)} />
    </Box>
  );
}
